import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User, Info } from 'lucide-react';
import { authClient } from '../lib/authClient';
import { clearSessionData } from '../utils/clearSessionData';
import { buildVersion } from '../serviceWorkerRegistration';
import { latestVersion } from '../utils/latestVersion';
import { useOfflineSession } from '../context/OfflineContext';

// placement: 'up' for the desktop sidebar's bottom corner, 'down' for the mobile header
export default function UserMenu({ placement = 'up' }) {
  const navigate  = useNavigate();
  const [open, setOpen] = useState(false);
  const [latest, setLatest] = useState(undefined);  // undefined = checking, null = couldn't check
  const version = buildVersion();
  const ref = useRef(null);

  const { data: session } = authClient.useSession();
  const { offlineSession } = useOfflineSession();
  // Offline, the session check can't run: fall back to the name saved with the last session
  const name = session?.user?.name || offlineSession?.name;
  const initials = name
    ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '?';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Check for a newer build each time the menu opens
  useEffect(() => {
    if (!open || !version) return;
    let live = true;
    setLatest(undefined);
    latestVersion().then(v => { if (live) setLatest(v); });
    return () => { live = false; };
  }, [open, version]);

  const versionStatus = latest === undefined ? 'Checking…'
    : latest === null ? "Can't check (no signal)"
    : latest === version ? 'Up to date'
    : 'Update available: close and reopen the app';

  const signOut = async () => {
    await authClient.signOut();
    await clearSessionData();
    navigate('/sign-in');
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={styles.avatar}
        title={session?.user?.email ?? ''}
      >
        {initials}
      </button>
      {open && (
        <div style={{ ...styles.dropdown, ...(placement === 'down' ? styles.dropdownDown : styles.dropdownUp) }}>
          <div style={styles.emailRow}>
            <User size={14} />
            <span style={{ marginLeft: 6, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {session?.user?.email}
            </span>
          </div>
          {version && (
            <div style={styles.versionRow}>
              <Info size={14} style={{ flexShrink: 0 }} />
              <span style={{ marginLeft: 6 }}>
                Version {version}
                <span style={{ display: 'block', color: latest && latest !== version ? '#b45309' : '#888' }}>
                  {versionStatus}
                </span>
              </span>
            </div>
          )}
          <button onClick={signOut} style={styles.signOutBtn}>
            <LogOut size={14} />
            <span style={{ marginLeft: 8 }}>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

const styles = {
  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#667eea',
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  dropdown: {
    position: 'absolute',
    background: '#fff',
    border: '1px solid #eee',
    borderRadius: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    minWidth: 210,
    zIndex: 1000,
    overflow: 'hidden',
  },
  dropdownUp: { bottom: '110%', left: 0 },
  dropdownDown: { top: '110%', right: 0 },
  emailRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    borderBottom: '1px solid #f0f0f0',
    color: '#555',
    maxWidth: 210,
  },
  versionRow: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '10px 14px',
    borderBottom: '1px solid #f0f0f0',
    color: '#555',
    fontSize: 12,
    lineHeight: 1.4,
  },
  signOutBtn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 14px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#333',
    fontSize: 14,
  },
};
